import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Input, Select, Spin } from "antd";
import { SearchOutlined, DatabaseOutlined } from "@ant-design/icons";
import { fetchDealershipInventory } from "../../services/api";
import styles from "./InventoryTableMock.module.css";

const { Option } = Select;

// Map API inventory item to table row shape
function mapInventoryItemToRow(item, index) {
  const id = item.id ?? item.vin ?? index;
  const year = item.year ?? "";
  const make = item.make ?? "";
  const model = item.model ?? "";
  const trim = item.trim ?? item.version ?? "";
  const title = [year, make, model, trim].filter(Boolean).join(" ") || "Vehicle";
  const mileageNum = item.mileage ?? item.miles ?? 0;
  const mileageStr = Number.isFinite(mileageNum)
    ? `${Number(mileageNum).toLocaleString()} miles`
    : "—";
  const vin = item.vin ?? "—";
  const thumbnail =
    item.image_url ??
    item.photo ??
    item.active_listings?.[0]?.photo ??
    "https://via.placeholder.com/60x40/2a2a2a/666?text=Car";

  const yourPrice = Number(item.price) ?? 0;
  const market =
    Number(item.market_price) ?? Number(item.market_avg_price) ?? yourPrice;
  const active = item.active_count ?? item.market_listing_count ?? item.active_listings?.length ?? 0;
  const sold = item.sold_count ?? item.market_sold_count ?? item.sold_listings?.length ?? 0;
  const deltaRaw =
    item.delta_to_market_pct ?? item.market_avg_delta ?? item.delta ?? (market ? ((yourPrice - market) / market) * 100 : 0);
  const delta = Number(deltaRaw) ?? 0;
  const deltaText = delta < 0 ? "below" : delta > 0 ? "above" : "";

  const priceStatus = item.price_status ?? (delta <= 0 ? "green" : "red");
  const tierRaw = item.bucket ?? item.tier;
  const tier =
    tierRaw != null
      ? String(tierRaw).toLowerCase()
      : (priceStatus === "green" && delta < 0
          ? "elite"
          : priceStatus === "green"
            ? "strong"
            : priceStatus === "yellow" || (delta > 0 && delta < 15)
              ? "watch"
              : "risk");

  const demand = item.demand_grade ?? item.demand ?? (delta <= 0 ? "A" : delta < 10 ? "B" : "C");
  const mds = item.mds ?? item.sold_dom_median ?? item.sold_dom ?? item.dom ?? 0;
  const domYour = item.dom ?? null;
  const domSold = item.sold_dom_median ?? item.sold_dom ?? null;
  const rank = item.rank_position ?? item.listing_rank ?? item.rank ?? 1;
  const rankTotal =
    item.rank_out_of ?? item.listing_comparables ??
    ((Number(item.active_count) || Number(item.market_listing_count) || 0) + (Number(item.sold_count) || Number(item.market_sold_count) || 0) || 1);
  const actionAmount =
    item.action_amount != null
      ? item.action_amount
      : (item.action ?? (market && yourPrice ? Math.round(market - yourPrice) : null));
  const actionText =
    item.action_label ?? item.action_text ?? item.action_type ??
    (actionAmount != null && actionAmount > 0 ? "Profit opportunity" : actionAmount != null && actionAmount < 0 ? "Reposition price" : "On market");

  return {
    id,
    tier,
    vehicle: { title, mileage: mileageStr, vin, thumbnail },
    yourPrice,
    market,
    active,
    sold,
    delta,
    deltaText,
    demand: String(demand).toUpperCase().slice(0, 1),
    mds,
    rank,
    rankTotal,
    domYour,
    domSold,
    action: actionAmount,
    actionText,
  };
}

// Normalize API response to array of items
function normalizeInventoryResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.inventory && Array.isArray(data.inventory)) return data.inventory;
  if (data?.data && Array.isArray(data.data)) return data.data;
  if (data?.items && Array.isArray(data.items)) return data.items;
  return [];
}

const TIER_SORT_ORDER = { risk: 0, watch: 1, strong: 2, elite: 3 };

const InventoryTableMock = ({ bucketFilter }) => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("risk");
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoadErrors, setImageLoadErrors] = useState(() => new Set());

  const loadInventory = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchDealershipInventory()
      .then((data) => {
        const items = normalizeInventoryResponse(data);
        setInventoryData(items.map((item, i) => mapInventoryItemToRow(item, i)));
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err?.message || "Failed to load inventory.");
        setInventoryData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const getTierColor = (tier) => {
    const colors = {
      elite: "#8b5cf6",
      strong: "#22c55e",
      watch: "#eab308",
      risk: "#ef4444",
    };
    return colors[tier] || colors.strong;
  };

  const getDemandColor = (grade) => {
    const letter = String(grade ?? "").trim().toUpperCase()[0];
    const colors = {
      A: "#22c55e",
      B: "#22c55e",
      C: "#eab308",
      D: "#ef4444",
      F: "#ef4444",
    };
    return colors[letter] || colors.B;
  };

  const filteredData = inventoryData.filter((row) => {
    const matchesSearch = !searchQuery || row.vehicle.title?.toLowerCase().includes(searchQuery.toLowerCase()) || row.vehicle.vin?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBucket = !bucketFilter || row.tier === bucketFilter;
    return matchesSearch && matchesBucket;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortBy === "risk") {
      const orderA = TIER_SORT_ORDER[a.tier] ?? 4;
      const orderB = TIER_SORT_ORDER[b.tier] ?? 4;
      return orderA - orderB;
    }
    if (sortBy === "dom") return (a.mds ?? 0) - (b.mds ?? 0);
    if (sortBy === "price") return a.yourPrice - b.yourPrice;
    return (a.rank ?? 0) - (b.rank ?? 0);
  });

  if (loading) {
    return (
      <div className={styles.inventoryTableContainer}>
        <div className={styles.tableHeaderControls}>
          <h3 className={styles.tableTitle}>Your Inventory</h3>
        </div>
        <div style={{ padding: "48px 16px", textAlign: "center" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const showEmptyState = !loading && (error || inventoryData.length === 0);

  if (showEmptyState) {
    return (
      <div className={styles.inventoryTableContainer}>
        <div className={styles.tableHeaderControls}>
          <h3 className={styles.tableTitle}>Your Inventory</h3>
        </div>
        <div className={styles.inventoryEmptyState}>
          <div className={styles.inventoryEmptyStateIconWrap}>
            <DatabaseOutlined className={styles.inventoryEmptyStateIcon} />
          </div>
          <div className={styles.inventoryEmptyStateContent}>
            <h4 className={styles.inventoryEmptyStateHeadline}>Inventory not loaded</h4>
            <p className={styles.inventoryEmptyStateDesc}>
              We were unable to fetch your inventory for this dealership. Contact support and we'll manually upload your inventory so Lot Performance can score your units.
            </p>
            <p className={styles.inventoryEmptyStateMuted}>Typical turnaround: same day.</p>
            <div className={styles.inventoryEmptyStateChips}>
              <span className={styles.inventoryEmptyStateChip}>Dealer website</span>
              <span className={styles.inventoryEmptyStateChip}>Inventory export (CSV)</span>
              <span className={styles.inventoryEmptyStateChip}>DMS provider (if any)</span>
            </div>
          </div>
          <div className={styles.inventoryEmptyStateActions}>
            <button type="button" className={styles.inventoryEmptyStateBtnPrimary} onClick={() => history.push("/support")}>
              Contact Support
            </button>
            <button type="button" className={styles.inventoryEmptyStateBtnSecondary} onClick={loadInventory}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.inventoryTableContainer}>
      {/* Header with Search and Sort */}
      <div className={styles.tableHeaderControls}>
        <h3 className={styles.tableTitle}>Your Inventory</h3>
        <div className={styles.tableControls}>
            <Select
            value={sortBy}
            onChange={setSortBy}
            className={styles.sortSelect}
            size="small"
          >
            <Option value="risk">Sort: Risk</Option>
            <Option value="rank">Sort: Rank</Option>
            <Option value="dom">Sort: DOM</Option>
            <Option value="price">Sort: Price</Option>
          </Select>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined className={styles.searchIcon} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
        </div>
      </div>

      {/* Table Header Row */}
      <div className={styles.tableHeaderRow}>
        <div className={`${styles.headerCell} ${styles.cellVehicle}`}>VEHICLE</div>
        <div className={`${styles.headerCell} ${styles.cellYourPrice}`}>YOUR PRICE</div>
        <div className={`${styles.headerCell} ${styles.cellMarket}`}>MARKET</div>
        <div className={`${styles.headerCell} ${styles.cellDelta}`}>Δ TO MARKET</div>
        <div className={`${styles.headerCell} ${styles.cellDemand}`}>DEMAND</div>
        <div className={`${styles.headerCell} ${styles.cellMds}`}>MDS</div>
        <div className={`${styles.headerCell} ${styles.cellDom}`}>RANK</div>
        <div className={`${styles.headerCell} ${styles.cellAction}`}>Your Dom / Sold Dom</div>
      </div>

      {/* Table Body */}
      <div className={styles.tableBody}>
        {sortedData.map((row) => (
          <div
            key={row.vehicle?.vin ?? row.id}
            className={`${styles.tableRow} ${styles[`row-${row.tier}`]}`}
            style={{ borderLeftColor: getTierColor(row.tier) }}
          >
            {/* Mobile-only card layout */}
            <div className={styles.mobileCard}>
              <div className={styles.mobileCardTop}>
                <div className={styles.mobileCardImageWrap}>
                  {!row.vehicle.thumbnail || imageLoadErrors.has(row.vehicle.vin ?? row.id) ? (
                    <div className={styles.mobileCardThumbPlaceholder}>No image</div>
                  ) : (
                    <img
                      src={row.vehicle.thumbnail}
                      alt={row.vehicle.title}
                      className={styles.mobileCardThumb}
                      onError={() => {
                        setImageLoadErrors((prev) => new Set(prev).add(row.vehicle.vin ?? row.id));
                      }}
                    />
                  )}
                  <span className={`${styles.mobileTierPill} ${styles[`mobilePill-${row.tier}`]}`}>
                    {row.tier.toUpperCase()}
                  </span>
                </div>
                <div className={styles.mobileCardHeadInfo}>
                  <div className={styles.mobileCardTitle}>{row.vehicle.title}</div>
                  <div className={styles.mobileCardMileage}>{row.vehicle.mileage}</div>
                  <div className={styles.mobileCardVin}>{row.vehicle.vin}</div>
                </div>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>Price</span>
                <span className={styles.mobileCardPrice}>{row.yourPrice > 0 ? `$${row.yourPrice.toLocaleString()}` : "—"}</span>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>% to Market</span>
                <span className={`${styles.mobileCardDelta} ${row.delta >= 0 ? styles.deltaDown : styles.deltaUp}`}>
                  {Math.abs(row.delta).toFixed(1)}% {row.deltaText}
                  {row.delta >= 0 && " ↓"}
                </span>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>Mkt Average</span>
                <span className={styles.mobileCardPrice}>{row.market > 0 ? `$${row.market.toLocaleString()}` : "—"}</span>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>Active/Sold</span>
                <span className={styles.mobileCardValue}>{row.active} / {row.sold}</span>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>Rank</span>
                <span className={styles.mobileCardValue}>#{row.rank} of {row.rankTotal}</span>
              </div>
              <div className={styles.mobileCardDivider} />
              <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardRowLeft}>DOM</span>
                <span className={styles.mobileCardValue}>
                  {row.domYour != null && row.domSold != null ? `${row.domYour} / ${row.domSold}` : row.domYour != null ? String(row.domYour) : "—"}
                </span>
              </div>
            </div>

            {/* Desktop: Vehicle Column */}
            <div className={`${styles.tableCell} ${styles.cellVehicle}`}>
              <div className={styles.vehicleContent}>
                <div className={styles.vehicleImageContainer}>
                  {!row.vehicle.thumbnail || imageLoadErrors.has(row.vehicle.vin ?? row.id) ? (
                    <div className={styles.vehicleThumbnailPlaceholder}>
                      No image available
                    </div>
                  ) : (
                    <img
                      src={row.vehicle.thumbnail}
                      alt={row.vehicle.title}
                      className={styles.vehicleThumbnail}
                      onError={() => {
                        setImageLoadErrors((prev) => new Set(prev).add(row.vehicle.vin ?? row.id));
                      }}
                    />
                  )}
                  <span
                    className={`${styles.tierBadge} ${styles[`badge-${row.tier}`]}`}
                  >
                    {row.tier.toUpperCase()}
                  </span>
                </div>
                <div className={styles.vehicleInfo}>
                  <div className={styles.vehicleTitle}>{row.vehicle.title}</div>
                  <div className={styles.vehicleMeta}>{row.vehicle.mileage}</div>
                  <div className={styles.vehicleVin}>{row.vehicle.vin}</div>
                </div>
              </div>
            </div>

            {/* Desktop: Your Price Column */}
            <div className={`${styles.tableCell} ${styles.cellYourPrice}`}>
              <div className={styles.priceValue}>
                ${row.yourPrice.toLocaleString()}
              </div>
            </div>

            {/* Desktop: Market Column */}
            <div className={`${styles.tableCell} ${styles.cellMarket}`}>
              <div className={styles.marketValue}>
                ${row.market.toLocaleString()}
              </div>
              <div className={styles.marketStats}>
                Active: {row.active} / Sold: {row.sold}
              </div>
            </div>

            {/* Desktop: Δ TO MARKET Column */}
            <div className={`${styles.tableCell} ${styles.cellDelta}`}>
              <div
                className={`${styles.deltaValue} ${
                  row.delta < 0 ? styles.deltaUp : styles.deltaDown
                }`}
              >
                {Math.abs(row.delta).toFixed(1)}% {row.deltaText}
                {row.delta >= 0 && <span className={styles.deltaArrow}>↓</span>}
              </div>
            </div>

            {/* Desktop: DEMAND Column */}
            <div className={`${styles.tableCell} ${styles.cellDemand}`}>
              <div
                className={styles.demandBadge}
                style={{
                  borderColor: getDemandColor(row.demand),
                  color: getDemandColor(row.demand),
                }}
              >
                {row.demand}
              </div>
            </div>

            {/* Desktop: MDS Column */}
            <div className={`${styles.tableCell} ${styles.cellMds}`}>
              <div className={styles.mdsValue}>{row.mds}</div>
            </div>

            {/* Desktop: RANK Column */}
            <div className={`${styles.tableCell} ${styles.cellDom}`}>
              <div className={styles.domValue}>
                #{row.rank} of {row.rankTotal}
              </div>
            </div>

            {/* Desktop: DOM Column (your dom vs sold dom) */}
            <div className={`${styles.tableCell} ${styles.cellAction}`}>
              <div className={styles.domVsValue}>
                {row.domYour != null && row.domSold != null
                  ? `${row.domYour} / ${row.domSold}`
                  : row.domYour != null
                    ? String(row.domYour)
                    : "—"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryTableMock;
